import React, { useMemo } from 'react';
import * as Yup from 'yup';

import { FixedFee, GlobalAccountFeeRule, PricingPackage } from '@ndvr/billing-api';

import { parseLocalDate, tomorrowDate, useRequiredParams } from 'utils';
import { FormSection, requiredStringValidator } from 'widgets';
import { FormDateInput } from 'widgets/form/input/FormDateInput';

import { useGlobalAccountFeeRuleTimeline, useSaveGlobalAccountFeeRule } from '../billing-queries';
import { getDisabledDates, getFirstAvailableDate, isDateDisabled } from '../billing-utils';
import { PricingSelect } from '../widgets/PricingSelect';
import { BillingDrawer } from './BillingDrawer';

interface Props {
    globalAccountFeeRule?: GlobalAccountFeeRule;
    pricingPackages: PricingPackage[];
    onClose: () => void;
}

export const PricingProfileDrawer = ({ globalAccountFeeRule, pricingPackages, onClose }: Props) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { mutate: saveGlobalAccountFeeRule, isPending } = useSaveGlobalAccountFeeRule(
        globalAccountGUID,
        !!globalAccountFeeRule,
    );

    const { data: timeline } = useGlobalAccountFeeRuleTimeline(globalAccountGUID);
    const disabledDates = getDisabledDates(
        timeline?.upcomingRules.filter(
            (rule) => rule.globalAccountFeeRuleGUID !== globalAccountFeeRule?.globalAccountFeeRuleGUID,
        ),
    );
    const firstAvailableDate = getFirstAvailableDate(disabledDates);

    const defaultValues = useMemo(() => {
        return globalAccountFeeRule
            ? { ...globalAccountFeeRule }
            : ({
                  globalAccountGUID,
                  pricingPackageGUID: pricingPackages[0]?.pricingPackageGUID,
                  startDate: firstAvailableDate,
              } as GlobalAccountFeeRule);
    }, [globalAccountFeeRule, globalAccountGUID, pricingPackages, firstAvailableDate]);

    const formSchema = Yup.object({
        pricingPackageGUID: Yup.string().test(
            'pricing-package-required',
            'Pricing package is required',
            (value, context) => {
                const { fixedFee, annualBps } = context.parent;
                return (fixedFee && annualBps) || value;
            },
        ),
        fixedFee: Yup.object({
            money: Yup.object({
                amount: Yup.string().when('pricingPackageGUID', {
                    is: undefined,
                    then: () => requiredStringValidator.label('Membership fee'),
                }),
            }),
        }).default(undefined),
        annualBps: Yup.string().when('pricingPackageGUID', {
            is: undefined,
            then: () => requiredStringValidator.label('AUM charge'),
        }),
        startDate: requiredStringValidator.test(
            'start-date-available',
            'Rule already exists with this start date',
            (startDate) => !isDateDisabled(parseLocalDate(startDate), disabledDates),
        ),
    });

    return (
        <BillingDrawer
            title={globalAccountFeeRule ? 'Update Pricing Profile' : 'Create New Pricing Profile'}
            editing={!!globalAccountFeeRule}
            formSchema={formSchema}
            defaultValues={defaultValues}
            onSubmit={saveGlobalAccountFeeRule}
            pending={isPending}
            onClose={onClose}
        >
            <FormSection title="Pricing Profile">
                <PricingSelect
                    pricingPackages={pricingPackages}
                    defaultRadioIndex={!globalAccountFeeRule || globalAccountFeeRule?.pricingPackageGUID ? 0 : 1}
                />
            </FormSection>
            <FormSection hasDivider>
                <FormDateInput
                    name="startDate"
                    label="Start Date"
                    minDate={tomorrowDate()}
                    description="The preceding pricing profile will terminate on this date. This pricing profile will only terminate if a future pricing profile is established."
                    disabledDates={disabledDates}
                />
            </FormSection>
        </BillingDrawer>
    );
};
